// src/components/PurchaseFlow/Steps/ConfirmationViews/Variant2ConfirmationView.js
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrency } from '../../../../utils/numberFormatter';
import { 
  faFileSignature,
  faCalculator,
  faDollarSign,
  faCalendar,
  faPercentage,
  faHandHoldingUsd,
  faCalendarCheck,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import QRCode from 'qrcode';

const MySwal = withReactContent(Swal);

const Variant2ConfirmationView = ({ 
  selectedOffer, 
  isProcessing, 
  onConfirmButtonClick,
  clientData
}) => {
  const handleShareMiCuenta = async () => {
    if (!clientData?.my_account_url) return;

    try {
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, clientData.my_account_url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const qrDataUrl = canvas.toDataURL();

      MySwal.fire({
        title: 'Portal Mi Cuenta',
        html: `
          <div class="text-center">
            <img src="${qrDataUrl}" class="mb-4" alt="QR Code" />
            <p class="mb-3">El cliente puede escanear este código QR para acceder a "Mi Cuenta", para realizar gestiones y consultas.</p>
            
            <div class="alert alert-warning d-flex align-items-center gap-2 mb-4 p-2" style="font-size: 0.9rem; max-width: 100%;">
              <div class="container-fluid">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                  <div class="text-start">
                    Este link es único y personal para <strong>${clientData.firstName} ${clientData.lastName}</strong>. 
                    No debe ser compartido con otros clientes.
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div class="row g-3">
              <div class="col-6">
                <p class="text-muted small mb-2">Copia este enlace:</p>
                <div class="input-group">
                  <input 
                    type="text" 
                    value="${clientData.my_account_url}" 
                    class="form-control form-control-sm"
                    readonly
                  />
                  <button 
                    class="btn btn-outline-secondary copy-button"
                    type="button"
                    style="border: 1px solid #dce7f1 !important"
                    onclick="copyToClipboard('${clientData.my_account_url}', this)"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
              <div class="col-6">
                <p class="text-muted small mb-2">O comparte con el cliente:</p>
                <a 
                  href="https://wa.me/?text=${encodeURIComponent(
                    `Hola ${clientData.firstName}, aquí puedes ver toda la información de tus préstamos y realizar gestiones: ${clientData.my_account_url}`
                  )}" 
                  target="_blank" 
                  class="btn btn-success w-100"
                  style="background-color: #25D366; border-color: #25D366"
                >
                  <i class="bi bi-whatsapp me-2"></i>
                  Enviar por WhatsApp
                </a>
              </div>
            </div>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        didOpen: () => {
          window.copyToClipboard = (text, button) => {
            navigator.clipboard.writeText(text).then(() => {
              const originalContent = button.innerHTML;
              button.innerHTML = '<i class="bi bi-check"></i>';
              button.disabled = true;
              setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
              }, 2000);
            });
          };
        }
      });
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  return (
    <div className="container rg-sale-confirmation-step">
      <h2 className="text-center rg-sale-step__title">
        <FontAwesomeIcon icon={faFileSignature} className="me-2" />
        Resumen del crédito otorgado
      </h2>
      <p className="text-center text-muted rg-sale-step__subtitle">Revisa los detalles antes de confirmar la operación.</p>

      <hr className="my-4" />

      <div className="row justify-content-center m-2">
        <div className="summary-section">
            <div className="summary-item">
                <div className="summary-label">
                    <FontAwesomeIcon icon={faDollarSign} />
                    Monto
                </div>
                <div className="summary-value">Se otorga un crédito por {formatCurrency(selectedOffer.originalAmount || selectedOffer.amount, 2)}</div>
            </div>

            <div className="summary-item">
                <div className="summary-label">
                    <FontAwesomeIcon icon={faCalendar} />
                    Cuotas
                </div>
                <div className="summary-value">En {selectedOffer.term} cuotas.</div>
            </div>

            <div className="summary-item">
                <div className="summary-label">
                    <FontAwesomeIcon icon={faCalendar} />
                    Inicio del pago
                </div>
                <div className="summary-value">La primera cuota vence el <strong>{selectedOffer.displayDate || '[mes siguiente]'}</strong>.</div>
            </div>
        </div>
      </div>

      <div className="row justify-content-center m-2">
        <div className="terms-section">
            <div className="terms-item">
                <div className="terms-icon text-primary">
                    <FontAwesomeIcon icon={faHandHoldingUsd} />
                </div>
                <div>
                    <p className="mb-0"><strong>Plataforma de autogestión BNPL</strong> (acceso en: <span 
                      onClick={handleShareMiCuenta} 
                      style={{ 
                        color: '#0d6efd', 
                        textDecoration: 'underline', 
                        cursor: 'pointer' 
                      }}
                    >Mi Cuenta</span>) para pagar con:</p>
                    <ul className="list-unstyled ps-3 mt-2">
                        <li className="mb-1">■ Transferencia bancaria</li>
                        <li className="mb-1">■ Mercado Pago</li>
                        <li className="mb-1">■ Pago Fácil</li>
                        <li className="mb-1">■ Cobro Express</li>
                    </ul>
                </div>
            </div>

            <div className="terms-item">
                <div className="terms-icon text-primary">
                    <FontAwesomeIcon icon={faCalendar} />
                </div>
                <div>
                <h6 className="mb-1 mt-1 rg-sale-section__title">Vencimientos y punitorios</h6>
                    <p className="mb-1">Fecha de vencimiento: <strong>1°</strong> de cada mes.</p>
                    <p className="mb-0">En caso de no recibir el pago en plazo determinado se cobrarán intereses punitorios.</p>
                </div>
            </div>
        </div>
      </div>

      <div className="row justify-content-center m-2">
        <div className="bg-light-info p-3 rounded-3">
          <strong>Importante:</strong> Recordá informarle al cliente el acceso a la plataforma de autogestión para que pueda ver su resumen y elegir cómo pagar.
        </div>
      </div>

      <div className="text-center mt-4">
        <button 
          className="btn btn-primary btn-lg rg-sale-confirmation-step__cta" 
          onClick={onConfirmButtonClick}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Procesando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              {selectedOffer?.isDownpaymentPlan ? 'Realizar Pago Inicial y Confirmar' : 'Confirmar y Generar Préstamo'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Variant2ConfirmationView;
